import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import PdfViewer from '../components/PdfViewer';
import './RagPage.css';

function RagPage() {
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState({ percent: 0, stage: '', message: '' });
  const eventSourceRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    loadDocuments();
    return () => {
      // Clean up SSE connection on unmount
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const loadDocuments = async () => {
    try {
      const docs = await api.getDocuments();
      setDocuments(docs);
    } catch (err) {
      console.error('Error loading documents:', err);
      setError('Erreur lors du chargement des documents');
    }
  };

  const cleanupUpload = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.pdf')) {
      setError('Veuillez sélectionner un fichier PDF');
      return;
    }

    // Clean up any previous connection
    cleanupUpload();

    setUploading(true);
    setError('');
    // Start with upload progress
    setUploadProgress({ percent: 0, stage: 'uploading', message: 'Téléchargement du fichier...' });

    try {
      // Upload with progress callback (now fast - just saves file)
      const result = await api.uploadDocument(file, (percent) => {
        // During upload, show 0-10% range
        setUploadProgress({
          percent: Math.min(percent * 0.1, 10),
          stage: 'uploading',
          message: `Téléchargement: ${percent}%`
        });
      });

      // After upload response, show that indexing is starting
      setUploadProgress({ percent: 10, stage: 'uploaded', message: 'Fichier téléchargé, indexation en cours...' });

      // Set a timeout to handle case where SSE never connects or indexing gets stuck
      timeoutRef.current = setTimeout(() => {
        console.warn('Indexing timeout - assuming complete or stuck');
        cleanupUpload();
        setUploading(false);
        loadDocuments();
      }, 300000); // 5 minutes timeout for indexing

      // Subscribe to indexing progress via SSE
      const eventSource = api.subscribeToIndexingProgress(
        result.id,
        // onProgress callback
        (progress) => {
          console.log('Progress update:', progress);

          setUploadProgress({
            percent: progress.percent,
            stage: progress.stage,
            message: progress.message || `${progress.stage}: ${progress.percent}%`
          });

          // Reset timeout on each progress update
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => {
              console.warn('Indexing timeout after progress stalled');
              cleanupUpload();
              setError("L'indexation semble bloquée. Veuillez réessayer.");
              setUploading(false);
              loadDocuments();
            }, 60000); // 1 minute timeout if no progress updates
          }

          // Close connection when complete or error
          if (progress.stage === 'complete' || progress.stage === 'error') {
            if (progress.stage === 'error') {
              setError(`Erreur lors de l'indexation: ${progress.message}`);
            }
            cleanupUpload();
            setTimeout(() => {
              setUploading(false);
              loadDocuments();
            }, 1000);
          }
        },
        // onError callback - handles SSE connection errors
        async (sseError, statusData) => {
          console.log('SSE error - checking status:', { sseError, statusData });

          if (statusData) {
            // We got status data - determine what to do
            if (statusData.status === 'complete') {
              setUploadProgress({ percent: 100, stage: 'complete', message: 'Indexation terminée' });
            } else if (statusData.status === 'error') {
              setError(`Erreur: ${statusData.message}`);
            } else if (statusData.status === 'file_only') {
              setError('Document téléchargé mais indexation incomplete. Veuillez réessayer.');
            } else {
              // Still indexing or unknown state
              setUploadProgress({
                percent: statusData.percent || 50,
                stage: statusData.status,
                message: statusData.message || 'Indexation en cours...'
              });
            }
          } else {
            // No status data - connection error
            setError('Erreur de connexion au serveur. L\'indexation peut continuer en arrière-plan.');
          }

          cleanupUpload();
          setTimeout(() => {
            setUploading(false);
            loadDocuments();
          }, 2000);
        }
      );

      eventSourceRef.current = eventSource;
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.detail || 'Erreur lors du téléchargement du document');
      cleanupUpload();
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) {
      return;
    }

    try {
      await api.deleteDocument(docId);
      await loadDocuments();
      if (selectedDoc && selectedDoc.id === docId) {
        setSelectedDoc(null);
      }
    } catch (err) {
      setError('Erreur lors de la suppression du document');
    }
  };

  const handleDownload = async (docId, filename) => {
    try {
      await api.downloadDocument(docId, filename);
    } catch (err) {
      setError('Erreur lors du téléchargement');
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm(
      'Êtes-vous sûr de vouloir supprimer TOUS les documents ?\n\n' +
      'Cette action va:\n' +
      '- Supprimer tous les documents de la base Qdrant\n' +
      '- Supprimer tous les fichiers PDF\n\n' +
      'Cette action est irréversible !'
    )) {
      return;
    }

    // Double confirmation
    if (!window.confirm('Confirmer la suppression de TOUS les documents ?')) {
      return;
    }

    try {
      const result = await api.clearAllDocuments();
      setError('');
      setSelectedDoc(null);
      await loadDocuments();
      alert(result.message || `Tous les documents ont été supprimés (${result.count} documents)`);
    } catch (err) {
      setError('Erreur lors de la suppression des documents');
    }
  };

  return (
    <div className="rag-page">
      <div className="rag-header">
        <h2>Documents RAG</h2>
        <div className="rag-header-actions">
          {documents.length > 0 && (
            <button
              className="btn-danger"
              onClick={handleClearAll}
              disabled={uploading}
              title="Supprimer tous les documents"
            >
              Tout supprimer
            </button>
          )}
          <label className={`btn-primary ${uploading ? 'disabled' : ''}`}>
            {uploading ? 'Téléchargement...' : 'Uploader un PDF'}
            <input
              type="file"
              accept=".pdf"
              onChange={handleFileUpload}
              disabled={uploading}
              style={{ display: 'none' }}
            />
          </label>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Progress Bar */}
      {uploading && (
        <div className="upload-progress-container">
          <div className="upload-progress-info">
            <span className="upload-progress-stage">{getStageLabel(uploadProgress.stage)}</span>
            <span className="upload-progress-percent">{uploadProgress.percent}%</span>
          </div>
          <div className="upload-progress-bar-bg">
            <div
              className="upload-progress-bar-fill"
              style={{ width: `${uploadProgress.percent}%` }}
            />
          </div>
          {uploadProgress.message && (
            <div className="upload-progress-message">{uploadProgress.message}</div>
          )}
        </div>
      )}

      <div className="rag-content">
        <div className="documents-list">
          <h3>Documents disponibles</h3>
          {documents.length === 0 ? (
            <p className="empty-state">Aucun document disponible</p>
          ) : (
            <ul className="doc-list">
              {documents.map((doc) => (
                <li key={doc.id} className={`doc-item ${selectedDoc?.id === doc.id ? 'active' : ''}`}>
                  <div className="doc-info" onClick={() => setSelectedDoc(doc)}>
                    <span className="doc-name">{doc.filename}</span>
                    <span className="doc-date">
                      {new Date(doc.upload_date).toLocaleDateString('fr-FR')}
                      {doc.chunk_count !== undefined && doc.chunk_count !== null && (
                        <span className="doc-chunks"> · {doc.chunk_count} chunk{doc.chunk_count > 1 ? 's' : ''}</span>
                      )}
                    </span>
                  </div>
                  <div className="doc-actions">
                    <button
                      onClick={() => handleDownload(doc.id, doc.filename)}
                      className="btn-icon"
                      title="Télécharger"
                    >
                      &#128190;
                    </button>
                    <button
                      onClick={() => handleDelete(doc.id)}
                      className="btn-icon btn-danger"
                      title="Supprimer"
                    >
                      &#128465;
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="pdf-viewer-container">
          {selectedDoc ? (
            <PdfViewer docId={selectedDoc.id} filename={selectedDoc.filename} />
          ) : (
            <div className="pdf-viewer-placeholder">
              <div className="placeholder-content">
                <div className="placeholder-icon">📄</div>
                <h3>Aucun document sélectionné</h3>
                <p>Cliquez sur un document dans la liste pour prévisualiser son contenu</p>
                {documents.length === 0 && (
                  <p className="placeholder-hint">Commencez par uploader un document PDF</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  function getStageLabel(stage) {
    const labels = {
      uploading: 'Téléchargement',
      uploaded: 'Upload terminé',
      extracting: 'Extraction du texte',
      chunking: 'Découpage en chunks',
      embedding: 'Génération des embeddings',
      storing: 'Stockage dans la base',
      complete: 'Terminé',
      error: 'Erreur'
    };
    return labels[stage] || stage;
  }
}

export default RagPage;
